import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PipelineCard as PipelineCardData } from '@/types/recruitment';
import {
  defaultAnimateLayoutChanges,
  useSortable,
  type AnimateLayoutChanges,
} from '@dnd-kit/sortable';
import { Link } from '@tanstack/react-router';
import { CSS } from '@dnd-kit/utilities';
import { Lock, MessageSquare, MoreVertical, Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';

interface PipelineCardProps {
  card: PipelineCardData;
  onOpenCard: (card: PipelineCardData) => void;
  dragDisabled?: boolean;
  isOverlay?: boolean;
}

function formatTimeInStage(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 24 * 60 * 60 * 1000) return 'New';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

export function PipelineCard({
  card,
  onOpenCard,
  dragDisabled,
  isOverlay,
}: PipelineCardProps) {
  const isRejected = card.result === 'REJECTED';
  // HW-480: a hired candidate is now an Employee. The card can still be dragged
  // (so it can be moved between Hired-mapped stages or back into the canonical
  // Hired column after an orphan move), but the backend rejects any drop into a
  // stage that isn't HIRED-mapped — the lock icon below signals that to users.
  const isHired = card.result === 'HIRED';
  // HW-494: rejected cards stay freely draggable — the Rejected column is a normal
  // column, and dragging a card out of it un-rejects the candidate.
  const effectiveDragDisabled = dragDisabled || isOverlay;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.candidateVacancyId,
    disabled: effectiveDragDisabled,
    animateLayoutChanges,
  });

  const didDragRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
  };

  const { candidate, stageEnteredAt } = card;
  const timeInStage = formatTimeInStage(new Date(stageEnteredAt));
  const fullName = `${candidate.firstName} ${candidate.lastName}`.trim();
  const hasIndicators = candidate.hasCv || candidate.notesCount > 0;

  const mergedListeners = effectiveDragDisabled
    ? {}
    : {
        ...listeners,
        onPointerDown: (e: React.PointerEvent) => {
          pointerStartRef.current = { x: e.clientX, y: e.clientY };
          didDragRef.current = false;
          listeners?.onPointerDown?.(e as never);
        },
        onPointerMove: (e: React.PointerEvent) => {
          if (pointerStartRef.current) {
            const dx = e.clientX - pointerStartRef.current.x;
            const dy = e.clientY - pointerStartRef.current.y;
            if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
              didDragRef.current = true;
            }
          }
        },
      };

  const handleClick = (e: React.MouseEvent) => {
    if (isOverlay || didDragRef.current) return;
    // Guard against clicks from kebab trigger or the portaled dropdown menu items.
    const target = e.target as HTMLElement;
    if (target.closest('[data-slot="card-kebab"], [role="menu"], [role="menuitem"]')) return;
    onOpenCard(card);
  };

  const cardBody = (
    <article
      ref={isOverlay ? undefined : setNodeRef}
      style={isOverlay ? undefined : style}
      {...(isOverlay ? {} : attributes)}
      {...mergedListeners}
      onClick={isOverlay ? undefined : handleClick}
      {...(!isOverlay && !isDragging && !isRejected && { 'data-slot': 'pipeline-card' })}
      className={cn(
        'group relative rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow select-none',
        !isOverlay && 'cursor-pointer',
        !isDragging && !isOverlay && 'hover:shadow-md',
        isOverlay && 'shadow-xl ring-2 ring-primary/20',
        isRejected && 'opacity-75 saturate-50',
      )}
    >
      <div className="flex items-start gap-2.5">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={candidate.photoUrl ?? undefined} />
          <AvatarFallback className="bg-muted text-[10px] font-semibold">
            {candidate.initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-medium text-foreground" title={fullName}>
              {fullName}
            </p>
            {isHired && (
              <Lock
                className="h-3 w-3 shrink-0 text-muted-foreground"
                aria-label="Hired — card locked"
              />
            )}
          </div>
          <span className="text-[11px] text-muted-foreground">{timeInStage}</span>
        </div>
        {!isOverlay && <KebabMenu candidateId={candidate.id} />}
      </div>

      {hasIndicators && (
        <div className="mt-2 flex items-center gap-3 pl-10.5 text-[11px] text-muted-foreground">
          {candidate.hasCv && (
            <span className="inline-flex items-center gap-1" title="CV attached">
              <Paperclip className="h-3 w-3 shrink-0" />
              CV
            </span>
          )}
          {candidate.notesCount > 0 && (
            <span
              className="inline-flex items-center gap-1"
              title={`${candidate.notesCount} note${candidate.notesCount > 1 ? 's' : ''}`}
            >
              <MessageSquare className="h-3 w-3 shrink-0" />
              <span className="tabular-nums">{candidate.notesCount}</span>
            </span>
          )}
        </div>
      )}
    </article>
  );

  if (isRejected && card.rejectionReason) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{cardBody}</TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="wrap-anywhere text-[11px] font-medium">
              Rejected — {card.rejectionReason.label}
            </div>
            {card.rejectionComment && (
              // Dark tooltip surface — `text-muted-foreground` is invisible here.
              <div className="wrap-anywhere text-[11px] text-background/75">
                {card.rejectionComment}
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardBody;
}

interface KebabMenuProps {
  candidateId: string;
}

function KebabMenu({ candidateId }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-slot="card-kebab"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-44"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem asChild>
          <Link
            to="/recruitment/candidates/$id"
            params={{ id: candidateId }}
            // Radix doesn't close the menu on middle-click.
            onAuxClick={(e) => {
              if (e.button === 1) setOpen(false);
            }}
          >
            Open full page
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
